import { type ReactNode, useId, useState } from 'react'
import { useIntl } from 'react-intl'

import { ChevronDownIcon, ZoomInIcon } from 'lucide-react'

import { SupportDiagnosticMarkdown } from '@/components/molecules/SupportDiagnosticMarkdown'
import { cn } from '@/lib/utils.ts'

export interface SupportThreadDiagnosticProps {
  readonly kind: 'diagnostic'
  readonly time: string
  readonly title: string
  readonly cause: string
  readonly dateLine: string
  readonly markdown: string
  readonly className?: string
}

const commandCode = (chunks: ReactNode[]) => (
  <code key="command" className="font-mono">
    {chunks}
  </code>
)

export const SupportThreadDiagnostic = ({
  time,
  title,
  cause,
  dateLine,
  markdown,
  className,
}: SupportThreadDiagnosticProps) => {
  const intl = useIntl()
  const titleId = useId()
  const bodyId = useId()
  const [expanded, setExpanded] = useState(false)

  return (
    <article
      aria-labelledby={titleId}
      className={cn(
        'grid grid-cols-[72px_minmax(0,1fr)] gap-1 border-b border-gray-200 bg-gray-100 py-4 pr-6',
        className
      )}>
      <span className="pt-3 text-center text-sm text-gray-900">{time}</span>
      <div className="flex min-w-0 flex-col overflow-hidden rounded-md border border-gray-600 bg-white">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 bg-gray-800 px-3.5 py-2.5">
          <ZoomInIcon aria-hidden className="size-4 text-white" />
          <h3 id={titleId} className="text-base font-bold text-white">
            {title}
          </h3>
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={bodyId}
            aria-describedby={titleId}
            onClick={() => {
              setExpanded((value) => !value)
            }}
            className="ml-auto inline-flex h-7 cursor-pointer items-center gap-1 rounded-[4px] border border-gray-500 px-2.5 text-sm font-bold text-white">
            <ChevronDownIcon aria-hidden className={cn('size-3', expanded && 'rotate-180')} />
            {intl.formatMessage({ id: expanded ? 'soutien.diagnostic.collapse' : 'soutien.diagnostic.expand' })}
          </button>
        </div>
        {expanded ? (
          <div id={bodyId} className="flex flex-col gap-3 px-6 py-5">
            <p className="text-sm text-gray-600">{dateLine}</p>
            <SupportDiagnosticMarkdown markdown={markdown} className="border-t border-gray-200 pt-3" />
            <p className="border-t border-gray-200 pt-3 text-sm leading-[1.4] text-gray-600">
              {intl.formatMessage({ id: 'soutien.diagnostic.footer' }, { code: commandCode })}
            </p>
          </div>
        ) : (
          <p id={bodyId} className="px-3.5 py-3 text-base leading-normal text-gray-800">
            <span className="font-bold text-gray-900">{intl.formatMessage({ id: 'soutien.diagnostic.cause' })}</span>{' '}
            {cause}
          </p>
        )}
      </div>
    </article>
  )
}
